import type {
  ApplyVendorCreditPayload,
  IssueVendorCreditPayload,
  PurchaseOrderForVendorCreditApplication,
  ReverseVendorCreditApplicationPayload,
  VendorAccountForCreditIssuance,
  VendorCredit,
  VendorCreditApplication,
  VendorCreditBalance,
  VendorCreditMutationResult,
} from "@/features/vendor-credits/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { buildAppliedAmountMap, sumAppliedAmount } from "@/lib/credit-application-balances";
import { orderListPage, tiebreakListOrder } from "@/lib/db/list-order";
import { readAllPages } from "@/lib/db/paged-read";
import { roundToCents } from "@/lib/money";
import { cast, firstRecord, getOptionalString, toNumber } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const VENDOR_CREDIT_SELECT = `
  id, amount, date, method, reference, account_id, purchase_order_id, created_at,
  gl_accounts!gl_vendor_credits_account_id_fkey ( id, name, uid ),
  gl_purchase_orders!gl_vendor_credits_purchase_order_id_fkey ( id, uid )
`;

const PURCHASE_ORDER_FOR_APPLICATION_SELECT = `
  id, uid, balance, account_id,
  gl_accounts!gl_purchase_orders_account_id_fkey ( id, name, uid )
`;

const VENDOR_CREDIT_APPLICATION_SELECT = `
  id, vendor_credit_id, purchase_order_id, amount_applied, is_reversal,
  applied_at, reverses_application_id, reference,
  gl_vendor_credits!gl_vendor_credit_applications_vendor_credit_id_fkey ( id ),
  gl_purchase_orders!gl_vendor_credit_applications_purchase_order_id_fkey ( id, uid )
`;

const VENDOR_CREDIT_BALANCE_SELECT = `
  id, amount, account_id,
  gl_accounts!gl_vendor_credits_account_id_fkey ( id, name, uid )
`;

function getRecordString(value: Record<string, unknown>, key: string): string {
  const candidate = value[key];
  return typeof candidate === "string" ? candidate : "";
}

interface AccountBalanceEntry {
  account_id: string;
  account_name: string | null;
  account_uid: string;
  total_applied: number;
  total_credited: number;
}

/**
 * Reads every row a vendor account balance is derived from, one page at a time.
 *
 * Same reason as the customer side: these two reads are netted into one figure
 * per vendor, so a response capped at the server's row limit silently
 * under-counts what has been applied and overstates the credit still available
 * to net against open purchase orders.
 */
async function readAllBalanceRows<TRow>(buildQuery: () => QueryBuilder<TRow>): Promise<TRow[]> {
  return readAllPages(async ({ limit, offset }) => {
    const { data, error } = await buildQuery().range(offset, offset + limit - 1);
    if (error) {
      throw new Error(error.message);
    }
    return data ?? [];
  });
}

function extractAccountInfo(credit: Record<string, unknown>): {
  accountId: string;
  accountName: string | null;
  accountUid: string;
} {
  const account = firstRecord(credit.gl_accounts);

  return {
    accountId: getRecordString(credit, "account_id"),
    accountName: getOptionalString(account, "name"),
    accountUid: getOptionalString(account, "uid") ?? "",
  };
}

/**
 * A credit's face value as a number.
 *
 * `numeric` columns can arrive as strings over PostgREST. The applied side of
 * this same balance already coerces, via `buildAppliedAmountMap`, so reading a
 * string amount as `0` here nets a real application against no credit at all
 * and reports the vendor's available credit as negative — a vendor owed money
 * reads as owing it back.
 */
function getCreditAmount(credit: Record<string, unknown>): number {
  return toNumber(credit.amount) ?? 0;
}

function accumulateCreditBalance(
  accountMap: Map<string, AccountBalanceEntry>,
  credit: Record<string, unknown>,
  appliedByCredit: Map<string, number>,
): void {
  if (!credit.account_id) {
    return;
  }

  const { accountId, accountName, accountUid } = extractAccountInfo(credit);
  const creditId = getRecordString(credit, "id");
  const creditAmount = getCreditAmount(credit);

  const entry = accountMap.get(accountId) ?? {
    account_id: accountId,
    account_name: accountName,
    account_uid: accountUid,
    total_credited: 0,
    total_applied: 0,
  };

  entry.total_credited = roundToCents(entry.total_credited + creditAmount);
  entry.total_applied = roundToCents(entry.total_applied + (appliedByCredit.get(creditId) ?? 0));
  accountMap.set(accountId, entry);
}

function resolveRows<T>(result: { data: unknown; error: { message: string } | null }): T {
  if (result.error) {
    throw new Error(result.error.message);
  }
  return cast<T>(result.data || []);
}

function assertRequired(value: unknown, message: string): void {
  if (!value) {
    throw new Error(message);
  }
}

/**
 * Stamps `is_reversed` onto applications from a set of already-reversed ids.
 *
 * Callers own the id set because its completeness is what makes the flag
 * trustworthy: a set built from a paged slice would understate reversal state
 * for any application whose reversal fell outside the page.
 */
function markReversedApplications(
  applications: VendorCreditApplication[],
  reversedApplicationIds: ReadonlySet<string>,
): VendorCreditApplication[] {
  return applications.map((application) => ({
    ...application,
    is_reversed: reversedApplicationIds.has(application.id),
  }));
}

/** Resolves which of `applicationIds` already have a reversal against them. */
async function fetchReversedApplicationIds(
  applicationIds: readonly string[],
): Promise<Set<string>> {
  if (applicationIds.length === 0) {
    return new Set<string>();
  }

  // Filtered by the ids under consideration rather than fetching every reversal:
  // the result is bounded by the caller's page size, so it cannot be cut short
  // by a server-side row cap the way an unfiltered list read could be.
  const { data, error } = await backend.data
    .from("gl_vendor_credit_applications")
    .select("reverses_application_id")
    .eq("is_reversal", true)
    .in("reverses_application_id", applicationIds);

  if (error) {
    // Deliberately not swallowed: an empty set would read as "nothing is
    // reversed" and re-enable the reverse action on rows the RPC will reject.
    throw new Error(error.message);
  }

  const reversedApplicationIds = new Set<string>();
  for (const row of cast<{ reverses_application_id: string | null }[]>(data || [])) {
    if (row.reverses_application_id) {
      reversedApplicationIds.add(row.reverses_application_id);
    }
  }
  return reversedApplicationIds;
}

function assertPositiveAmount(amount: number): void {
  if (!(amount > 0)) {
    throw new Error("Amount must be positive");
  }
}

async function callVendorCreditRpc(
  rpcName: string,
  rpcParams: Record<string, unknown>,
  resultIdField: string,
  missingIdMessage: string,
): Promise<{ id: string; credit_remaining: number | null }> {
  const { data: rpcResult, error: rpcError } = await backend.data.rpc(rpcName, rpcParams);

  if (rpcError) {
    throw new Error(rpcError.message);
  }

  const rpcRecord = firstRecord(rpcResult);
  const id = rpcRecord ? getOptionalString(rpcRecord, resultIdField) : null;

  if (!id) {
    throw new Error(missingIdMessage);
  }

  return {
    id,
    credit_remaining: rpcRecord ? toNumber(rpcRecord.credit_remaining) : null,
  };
}

/** Lists recently issued vendor credits. */
export async function getVendorCredits(params?: {
  limit?: number;
  offset?: number;
}): Promise<VendorCredit[]> {
  const limit = params?.limit ?? 100;
  const offset = Math.max(0, params?.offset ?? 0);

  const query = orderListPage(backend.data.from("gl_vendor_credits").select(VENDOR_CREDIT_SELECT), {
    ascending: false,
    column: "date",
  }).range(offset, offset + limit - 1);

  return resolveRows<VendorCredit[]>(await query);
}

/** Loads one vendor credit with applications and computed available balance. */
export async function getVendorCreditById(creditId: string): Promise<
  VendorCredit & {
    available_balance: number;
    applications: VendorCreditApplication[];
  }
> {
  const [creditRes, appsRes] = await Promise.all([
    backend.data.from("gl_vendor_credits").select(VENDOR_CREDIT_SELECT).eq("id", creditId).single(),
    backend.data
      .from("gl_vendor_credit_applications")
      .select(VENDOR_CREDIT_APPLICATION_SELECT)
      .eq("vendor_credit_id", creditId)
      .order("applied_at", { ascending: false }),
  ]);

  if (creditRes.error) {
    throw new Error(creditRes.error.message);
  }
  if (appsRes.error) {
    throw new Error(appsRes.error.message);
  }

  const applications = cast<VendorCreditApplication[]>(appsRes.data || []);

  const totalApplied = sumAppliedAmount(applications);

  const credit = cast<VendorCredit>(creditRes.data);
  const available_balance = roundToCents((credit.amount || 0) - totalApplied);

  // No extra read here: this query is unpaged and scoped to one credit, so it
  // already contains every reversal carrying this vendor_credit_id -- unlike
  // the paged list read in getVendorCreditApplications, which needs
  // fetchReversedApplicationIds. The gl_vendor_credit_applications guard
  // trigger keeps a reversal's vendor_credit_id equal to that of the
  // application it reverses
  // (20260811210000_guard-vendor-credit-reversal-linkage.sql), except for
  // archive replay under xan.bypass_document_guards.
  const reversedApplicationIds = new Set(
    applications
      .filter((application) => application.is_reversal && application.reverses_application_id)
      .map((application) => application.reverses_application_id as string),
  );

  return {
    ...credit,
    available_balance,
    applications: markReversedApplications(applications, reversedApplicationIds),
  };
}

/** Lists vendor-credit applications with optional purchase-order filtering. */
export async function getVendorCreditApplications(params?: {
  purchase_order_id?: string;
  limit?: number;
  offset?: number;
}): Promise<VendorCreditApplication[]> {
  const limit = params?.limit ?? 200;
  const offset = Math.max(0, params?.offset ?? 0);

  let query = orderListPage(
    backend.data.from("gl_vendor_credit_applications").select(VENDOR_CREDIT_APPLICATION_SELECT),
    { ascending: false, column: "applied_at" },
  ).range(offset, offset + limit - 1);

  if (params?.purchase_order_id) {
    query = query.eq("purchase_order_id", params.purchase_order_id);
  }

  const applications = resolveRows<VendorCreditApplication[]>(await query);

  // Second read rather than a page-local scan: this list is paged, so a
  // reversal can be absent from it while the application it reverses is
  // present. One extra query regardless of row count, keyed on the page's ids.
  const reversedApplicationIds = await fetchReversedApplicationIds(
    applications.map((application) => application.id),
  );

  return markReversedApplications(applications, reversedApplicationIds);
}

/** Computes vendor credit totals and available credit by vendor account. */
export async function getVendorCreditBalances(): Promise<VendorCreditBalance[]> {
  const [creditRows, applicationRows] = await Promise.all([
    readAllBalanceRows(() =>
      tiebreakListOrder(
        backend.data.from("gl_vendor_credits").select(VENDOR_CREDIT_BALANCE_SELECT),
      ),
    ),
    readAllBalanceRows(() =>
      tiebreakListOrder(
        backend.data
          .from("gl_vendor_credit_applications")
          .select("vendor_credit_id, amount_applied, is_reversal"),
      ),
    ),
  ]);

  const appliedByCredit = buildAppliedAmountMap(applicationRows, "vendor_credit_id");

  const accountMap = new Map<string, AccountBalanceEntry>();

  for (const credit of cast<Record<string, unknown>[]>(creditRows)) {
    accumulateCreditBalance(accountMap, credit, appliedByCredit);
  }

  return Array.from(accountMap.values()).map((entry) => ({
    ...entry,
    available_credit: roundToCents(entry.total_credited - entry.total_applied),
  }));
}

async function fetchPurchaseOrdersForVendorCredit(
  params: { account_id?: string; limit?: number; offset?: number } | undefined,
  options: { positiveBalanceOnly: boolean },
): Promise<PurchaseOrderForVendorCreditApplication[]> {
  const limit = params?.limit ?? 50;
  const offset = Math.max(0, params?.offset ?? 0);
  const accountId = params?.account_id;

  let baseQuery = backend.data
    .from("gl_purchase_orders")
    .select(PURCHASE_ORDER_FOR_APPLICATION_SELECT);

  if (options.positiveBalanceOnly) {
    baseQuery = baseQuery.gt("balance", 0);
  }

  baseQuery = orderListPage(baseQuery, { ascending: false, column: "date" }).range(
    offset,
    offset + limit - 1,
  );

  const query = accountId ? baseQuery.eq("account_id", accountId) : baseQuery;

  return resolveRows<PurchaseOrderForVendorCreditApplication[]>(await query);
}

/** Lists positive-balance purchase orders eligible to receive vendor credit. */
export async function getPurchaseOrdersForVendorCreditApplication(params?: {
  account_id?: string;
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderForVendorCreditApplication[]> {
  return await fetchPurchaseOrdersForVendorCredit(params, {
    positiveBalanceOnly: true,
  });
}

/** Lists purchase orders that may be referenced while issuing vendor credit. */
export async function getPurchaseOrdersForCreditIssuance(params?: {
  account_id?: string;
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderForVendorCreditApplication[]> {
  return await fetchPurchaseOrdersForVendorCredit(params, {
    positiveBalanceOnly: false,
  });
}

/** Lists vendor accounts that can receive manually issued vendor credit. */
export async function getVendorAccountsForCreditIssuance(params?: {
  search?: string;
  limit?: number;
}): Promise<VendorAccountForCreditIssuance[]> {
  const limit = params?.limit ?? 50;

  let query = backend.data
    .from("gl_accounts")
    .select("id, name, uid")
    .eq("is_vendor", true)
    .order("name", { ascending: true })
    .limit(limit);

  if (params?.search?.trim()) {
    query = query.ilike("name", `%${params.search.trim()}%`);
  }

  return resolveRows<VendorAccountForCreditIssuance[]>(await query);
}

/** Calls the vendor-credit application RPC after validating the client payload. */
export async function applyVendorCredit(
  payload: ApplyVendorCreditPayload,
): Promise<VendorCreditMutationResult> {
  assertRequired(payload.vendor_credit_id, "vendor_credit_id is required");
  assertRequired(payload.purchase_order_id, "purchase_order_id is required");
  assertPositiveAmount(payload.amount);

  const { id, credit_remaining } = await callVendorCreditRpc(
    "xan_apply_vendor_credit",
    {
      p_vendor_credit_id: payload.vendor_credit_id,
      p_amount: payload.amount,
      p_purchase_order_id: payload.purchase_order_id,
    },
    "application_id",
    "Vendor credit application did not return an id.",
  );

  return { application_id: id, credit_remaining };
}

/** Calls the vendor-credit reversal RPC for an existing application id. */
export async function reverseVendorCreditApplication(
  payload: ReverseVendorCreditApplicationPayload,
): Promise<VendorCreditMutationResult> {
  assertRequired(payload.application_id, "application_id is required");

  const { id, credit_remaining } = await callVendorCreditRpc(
    "xan_reverse_vendor_credit_application",
    { p_application_id: payload.application_id },
    "reversal_id",
    "Vendor credit reversal did not return a reversal id.",
  );

  return { reversal_id: id, credit_remaining };
}

/** Calls the manual vendor-credit issuance RPC after validating the payload. */
export async function issueVendorCredit(
  payload: IssueVendorCreditPayload,
): Promise<VendorCreditMutationResult> {
  assertRequired(payload.account_id, "account_id is required");
  assertPositiveAmount(payload.amount);

  const { id, credit_remaining } = await callVendorCreditRpc(
    "xan_create_vendor_credit",
    {
      p_account_id: payload.account_id,
      p_amount: payload.amount,
      p_purchase_order_id: payload.purchase_order_id || undefined,
      p_date: payload.date || undefined,
      p_method: payload.method || undefined,
      p_reference: payload.reference || undefined,
    },
    "credit_id",
    "Vendor credit issuance did not return a credit id.",
  );

  return { credit_id: id, credit_remaining };
}
